import { useCallback } from 'react';

import {
  createEnumArrayParam,
  useQueryParam,
  withDefault,
} from 'use-query-params';

import { useLocalStorageValidState } from '@packages/hooks/use-local-storage-valid-state/use-local-storage-valid-state';

import type { AssetsTableColumnId } from '../asset-table.constants';

const SAVED_ASSET_TABLE_COLUMNS_KEY = 'assets-table-columns';

export const useSavedAssetTableColumns = (
  availableColumns: AssetsTableColumnId[],
  optionalColumns: AssetsTableColumnId[] = [],
) => {
  const filterToOnlyAvailableColumns = useCallback(
    (columns: AssetsTableColumnId[] | undefined) => {
      return Array.isArray(columns)
        ? columns.filter((column) => availableColumns.includes(column))
        : undefined;
    },
    [availableColumns],
  );

  const [localStorageColumns, setLocalStorageColumns] =
    useLocalStorageValidState(
      SAVED_ASSET_TABLE_COLUMNS_KEY,
      filterToOnlyAvailableColumns,
    );

  const defaultColumns = withDefault(
    createEnumArrayParam(availableColumns),
    localStorageColumns ??
      availableColumns.filter((column) => !optionalColumns.includes(column)),
  );

  const [columnsParam, setColumnsParam] = useQueryParam(
    'columns',
    defaultColumns,
  );

  const handleSetColumnsParam = useCallback(
    (newColumns: AssetsTableColumnId[]) => {
      const filteredNewColumns = filterToOnlyAvailableColumns(newColumns);
      setColumnsParam(filteredNewColumns);
      setLocalStorageColumns(filteredNewColumns);
    },
    [filterToOnlyAvailableColumns, setColumnsParam, setLocalStorageColumns],
  );

  return {
    savedAssetTableColumns: columnsParam,
    setSavedAssetTableColumns: handleSetColumnsParam,
  };
};
